/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import Monster from 'model/monster/Monster';

type AbilityArrayKeys = {
  [K in keyof Monster]: Monster[K] extends Ability[] ? K : never
}[keyof Monster];

export interface SectionManagement {
  sectionOptions: Map<string, AbilityArrayKeys>;
  handleSectionChange: (newSectionName: string, ability: Ability, oldSectionName: string) => void;
}

const useSectionManagement = (
  mon: Monster,
  handleMonsterMutated: () => void,
  setOpenBlock: (openBlock: string) => void,
  setOpenIndex: (openIndex: number) => void,
): SectionManagement => {
  const sectionOptions = new Map<string, AbilityArrayKeys>();

  sectionOptions.set('Traits',            'traits');
  sectionOptions.set('Actions',           'actions');
  sectionOptions.set('Spellcasting',      'spells');
  sectionOptions.set('Bonus Actions',     'bonus_actions');
  sectionOptions.set('Reactions',         'reactions');
  sectionOptions.set('Legendary Actions', 'legendary_actions');
  sectionOptions.set('Mythic',            'mythic_actions');
  sectionOptions.set('Regional Effects',  'regional_actions');
  sectionOptions.set('Lair Actions',      'lair_actions');
  sectionOptions.set('Inventory',         'inventory');

  const handleSectionChange = (newSectionName: string, ability: Ability, oldSectionName: string) => {
    if (!ability.name) {
      ability.name = '';
    }
    console.log(`Section ${ability.name} moving from ${oldSectionName} to ${newSectionName}`);

    const oldSectionVar = oldSectionName ? sectionOptions.get(oldSectionName) : sectionOptions.get('Abilities');
    const newSectionVar = newSectionName ? sectionOptions.get(newSectionName) : sectionOptions.get('Abilities');

    if (newSectionVar && oldSectionVar) {
      // Access the sections in the `Monster` object
      const oldSection = mon[oldSectionVar] as Ability[];
      const newSection = mon[newSectionVar] as Ability[];

      // Type guard to ensure both oldSection and newSection are arrays of Ability
      if (Array.isArray(oldSection) && Array.isArray(newSection)) {

        // Find the ability in the old section
        const index = oldSection.indexOf(ability);

        if (index > -1) {
          // Remove the ability from the old section
          oldSection.splice(index, 1);

          ability.id = (newSection.reduce((acc: number, curr: Ability) => ((curr.id && (curr.id > acc)) ? curr.id : acc), 0) + 1);

          // Add the ability to the new section
          newSection.push(ability);

          // Update the monster object with the modified sections
          mon[oldSectionVar] = oldSection;
          mon[newSectionVar] = newSection;

          // Notify parent that monster was mutated so hash gets recalculated
          handleMonsterMutated();

          setOpenBlock(newSectionName === 'Abilities' ? '' : newSectionName);
          setOpenIndex(newSection.length-1);
        }
      } else {
        console.error("One of the sections is not an array of abilities.");
      }
    }
  };

  return { sectionOptions, handleSectionChange };
};

export default useSectionManagement;
